import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, ExternalLink, X } from "lucide-react";
import { MaskedLines, Reveal, Overline, EASE } from "@/components/Motion";
import { api, imgSrc } from "@/lib/api";

const CaseStudy = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null); // null = loading, false = not found
  const [next, setNext] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (lightbox === null || !project || !project.gallery || project.gallery.length === 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % project.gallery.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + project.gallery.length) % project.gallery.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, project]);

  useEffect(() => {
    setProject(null);
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => setProject(false));
    api.get("/projects").then((r) => {
      const idx = r.data.findIndex((p) => p.id === id);
      if (idx !== -1 && r.data.length > 1) setNext(r.data[(idx + 1) % r.data.length]);
    }).catch(() => {});
  }, [id]);

  if (project === null) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20" data-testid="case-loading">
        <div className="w-8 h-8 rounded-full border-2 border-white/15 border-t-brand-cyan animate-spin" />
      </div>
    );
  }

  if (project === false) {
    return (
      <div className="pt-48 pb-32 max-w-7xl mx-auto px-6 lg:px-10" data-testid="case-not-found">
        <Overline>Case study</Overline>
        <h1 className="font-sora font-extrabold tracking-tighter text-4xl sm:text-6xl mt-8">Project not found.</h1>
        <Link to="/work" data-testid="case-back-link" className="mt-8 inline-flex items-center gap-2 text-brand-cyan font-semibold">
          <ArrowLeft className="w-4 h-4" /> Back to all work
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-40 pb-24" data-testid="case-study-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Link to="/work" data-testid="case-back-link" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors duration-300">
            <ArrowLeft className="w-4 h-4" /> All work
          </Link>
        </Reveal>

        <div className="mt-10 flex flex-wrap items-end justify-between gap-8">
          <div>
            <Overline>Case study</Overline>
            <MaskedLines
              as="h1"
              className="mt-6 font-sora font-extrabold tracking-tighter leading-[1.0] text-5xl sm:text-7xl lg:text-8xl"
              lines={[project.title]}
            />
            <Reveal delay={0.2}>
              <div className="flex flex-wrap gap-2 mt-6">
                {project.tags.map((t) => (
                  <span key={t} className="text-xs uppercase tracking-[0.15em] border border-white/20 rounded-full px-3 py-1 text-white/60">
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
          {project.live_url && (
            <Reveal delay={0.3}>
              <a
                href={project.live_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="case-live-link"
                className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white/80 transition-colors duration-300 hover:border-brand-cyan/60 hover:text-white"
              >
                Visit live site
                <ExternalLink className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </Reveal>
          )}
        </div>

        <Reveal className="mt-16">
          <div className="rounded-3xl overflow-hidden border border-white/10" data-testid="case-hero-image">
            <img src={imgSrc(project.image_url)} alt={project.title} className="w-full aspect-[16/8] object-cover" />
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mt-14 max-w-3xl text-lg md:text-xl text-white/70 leading-relaxed" data-testid="case-intro">
            {project.description}
          </p>
        </Reveal>

        {(project.challenge || project.results) && (
          <div className="mt-24 grid md:grid-cols-2 gap-14" data-testid="case-chapters">
            {project.challenge && (
              <Reveal>
                <div className="border-t border-white/10 pt-10">
                  <span className="font-sora font-extrabold text-6xl text-outline leading-none">01</span>
                  <h2 className="font-sora font-bold text-2xl md:text-3xl tracking-tight mt-6">The challenge</h2>
                  <p className="mt-4 text-white/60 leading-relaxed" data-testid="case-challenge">{project.challenge}</p>
                </div>
              </Reveal>
            )}
            {project.results && (
              <Reveal delay={0.12}>
                <div className="border-t border-white/10 pt-10">
                  <span className="font-sora font-extrabold text-6xl text-outline leading-none">02</span>
                  <h2 className="font-sora font-bold text-2xl md:text-3xl tracking-tight mt-6">The results</h2>
                  <p className="mt-4 text-white/60 leading-relaxed" data-testid="case-results">{project.results}</p>
                </div>
              </Reveal>
            )}
          </div>
        )}

        {project.gallery && project.gallery.length > 0 && (
          <div className="mt-24 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="case-gallery">
            {project.gallery.map((g, i) => (
              <Reveal key={i} delay={i * 0.1} className={i % 3 === 1 ? "lg:translate-y-10" : ""}>
                <button
                  onClick={() => setLightbox(i)}
                  data-cursor="view"
                  data-testid={`gallery-thumb-${i}`}
                  aria-label={`Open photo ${i + 1} full-screen`}
                  className="block w-full rounded-2xl overflow-hidden border border-white/10 group"
                >
                  <img
                    src={imgSrc(g)}
                    alt={`${project.title} — gallery ${i + 1}`}
                    loading="lazy"
                    className="w-full aspect-[4/5] object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.05]"
                  />
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      {next && (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-32">
          <Reveal>
            <Link to={`/work/${next.id}`} data-testid="case-next-project" data-cursor="view" className="group block border-t border-white/10 pt-14">
              <p className="text-xs uppercase tracking-[0.25em] text-white/40">Next project</p>
              <span className="font-sora font-extrabold tracking-tighter text-4xl sm:text-6xl mt-4 block">
                {next.title}
                <ArrowUpRight className="inline-block ml-4 w-10 h-10 text-brand-cyan transition-transform duration-300 group-hover:translate-x-2 group-hover:-translate-y-2" />
              </span>
            </Link>
          </Reveal>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-28">
        <Reveal>
          <div className="rounded-3xl border border-white/10 bg-gradient-to-r from-brand-blue/10 to-brand-purple/10 p-10 md:p-16 flex flex-wrap items-center justify-between gap-8">
            <h2 className="font-sora font-extrabold tracking-tighter text-3xl md:text-4xl max-w-lg leading-[1.1]">
              Want results like <span className="text-gradient">these?</span>
            </h2>
            <Link
              to="/contact"
              data-testid="case-cta-quote"
              className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple px-8 py-4 font-semibold transition-transform duration-300 hover:scale-[1.04]"
            >
              Get a Free Quote
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {lightbox !== null && project.gallery?.[lightbox] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[110] bg-midnight/95 backdrop-blur-xl flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              data-testid="lightbox-close"
              aria-label="Close full-screen photo"
              className="absolute top-6 right-6 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/70 transition-colors duration-300 hover:text-white hover:border-white/50"
            >
              <X className="w-5 h-5" />
            </button>
            {project.gallery.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + project.gallery.length) % project.gallery.length); }}
                  data-testid="lightbox-prev"
                  aria-label="Previous photo"
                  className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/70 transition-colors duration-300 hover:text-white hover:border-white/50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % project.gallery.length); }}
                  data-testid="lightbox-next"
                  aria-label="Next photo"
                  className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/70 transition-colors duration-300 hover:text-white hover:border-white/50"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              src={imgSrc(project.gallery[lightbox])}
              alt={`${project.title} — photo ${lightbox + 1}`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-full rounded-2xl border border-white/10 object-contain"
              data-testid="lightbox-image"
            />
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs uppercase tracking-[0.3em] text-white/40" data-testid="lightbox-counter">
              {lightbox + 1} / {project.gallery.length}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CaseStudy;
